export const normalizeSearchText = (value: unknown) =>
  String(value ?? '')
    .normalize('NFD')
    .replaceAll(/[\u0300-\u036F]/g, '')
    .toLowerCase()
    .replaceAll(/[^\da-z]+/g, ' ')
    .trim();

const searchTokens = (value: unknown) =>
  normalizeSearchText(value).split(/\s+/).filter(Boolean);

export type FuzzySearchIndexEntry<T> = {
  item: T;
  normalizedText: string;
  tokens: string[];
};

const maxDistanceForToken = (token: string) => {
  if (token.length <= 2) return 0;
  if (token.length <= 4) return 1;
  if (token.length <= 10) return 2;
  return 3;
};

const isNumericToken = (token: string) => /^\d+$/.test(token);

export const levenshteinDistance = (
  firstValue: string,
  secondValue: string,
  maxDistance = Number.POSITIVE_INFINITY
) => {
  const first = normalizeSearchText(firstValue);
  const second = normalizeSearchText(secondValue);

  if (first === second) return 0;
  if (!first) return second.length;
  if (!second) return first.length;
  if (Math.abs(first.length - second.length) > maxDistance) {
    return maxDistance + 1;
  }

  let previousRow = Array.from(
    { length: second.length + 1 },
    (_, index) => index
  );

  for (let firstIndex = 1; firstIndex <= first.length; firstIndex += 1) {
    const currentRow = [firstIndex];
    let rowMinimum = currentRow[0];

    for (let secondIndex = 1; secondIndex <= second.length; secondIndex += 1) {
      const substitutionCost =
        first[firstIndex - 1] === second[secondIndex - 1] ? 0 : 1;
      const distance = Math.min(
        currentRow[secondIndex - 1] + 1,
        previousRow[secondIndex] + 1,
        previousRow[secondIndex - 1] + substitutionCost
      );

      currentRow[secondIndex] = distance;
      rowMinimum = Math.min(rowMinimum, distance);
    }

    if (rowMinimum > maxDistance) return maxDistance + 1;
    previousRow = currentRow;
  }

  return previousRow[second.length];
};

const tokenScore = (queryToken: string, candidateToken: string) => {
  if (!queryToken || !candidateToken) return null;
  if (candidateToken === queryToken) return 0;
  if (candidateToken.startsWith(queryToken)) return 1;
  if (candidateToken.includes(queryToken)) return 2;
  if (queryToken.includes(candidateToken) && candidateToken.length >= 3) {
    return 3;
  }
  if (isNumericToken(queryToken) || isNumericToken(candidateToken)) return null;

  const maxDistance = maxDistanceForToken(queryToken);
  const distance = levenshteinDistance(queryToken, candidateToken, maxDistance);
  return distance <= maxDistance ? 4 + distance : null;
};

export const fuzzySearchScore = (query: string, candidate: unknown) => {
  const normalizedQuery = normalizeSearchText(query);
  const normalizedCandidate = normalizeSearchText(candidate);
  if (!normalizedQuery) return 0;
  if (!normalizedCandidate) return null;
  if (normalizedCandidate.includes(normalizedQuery)) return 0;

  return fuzzySearchScoreForTokens(
    searchTokens(normalizedQuery),
    searchTokens(normalizedCandidate)
  );
};

const fuzzySearchScoreForTokens = (
  queryTokens: string[],
  candidateTokens: string[]
) => {
  let score = 0;

  for (const queryToken of queryTokens) {
    let bestScore: number | null = null;

    for (const candidateToken of candidateTokens) {
      const candidateScore = tokenScore(queryToken, candidateToken);
      if (candidateScore === null) continue;
      bestScore =
        bestScore === null
          ? candidateScore
          : Math.min(bestScore, candidateScore);
    }

    if (bestScore === null) return null;
    score += bestScore;
  }

  return score;
};

export const fuzzyTextMatch = (query: string, candidate: unknown) =>
  fuzzySearchScore(query, candidate) !== null;

export const createFuzzySearchIndex = <T>(
  items: T[],
  stringify: (item: T) => unknown
): Array<FuzzySearchIndexEntry<T>> =>
  items.map((item) => {
    const normalizedText = normalizeSearchText(stringify(item));

    return {
      item,
      normalizedText,
      tokens: normalizedText.split(/\s+/).filter(Boolean),
    };
  });

export const fuzzyIndexMatch = <T>(
  query: string,
  entry: FuzzySearchIndexEntry<T>
) => {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return true;
  if (!entry.normalizedText) return false;
  if (entry.normalizedText.includes(normalizedQuery)) return true;

  return (
    fuzzySearchScoreForTokens(searchTokens(normalizedQuery), entry.tokens) !==
    null
  );
};

export const filterFuzzyOptions = <T>(
  options: T[],
  query: string,
  stringify: (option: T) => unknown,
  limit = 80
) => {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return options.slice(0, limit);

  return options
    .map((option, index) => ({
      index,
      option,
      score: fuzzySearchScore(normalizedQuery, stringify(option)),
    }))
    .filter(
      (entry): entry is { index: number; option: T; score: number } =>
        entry.score !== null
    )
    .sort(
      (first, second) =>
        first.score - second.score || first.index - second.index
    )
    .slice(0, limit)
    .map((entry) => entry.option);
};
